import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Target, Calendar, DollarSign, TrendingUp, RefreshCw, Plus, Check } from "lucide-react";
import { useBusinessPlan } from "../../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubsidiaryPlansModal = ({ 
  open, 
  onClose, 
  subsidiary,
  currentPlan,
  onSwitchPlan
}) => {
  const [loading, setLoading] = useState(false);
  const [businessPlans, setBusinessPlans] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const { createNewPlan } = useBusinessPlan();

  useEffect(() => {
    if (open && subsidiary) {
      fetchSubsidiaryPlans();
    }
  }, [open, subsidiary]);

  const fetchSubsidiaryPlans = async () => {
    if (!subsidiary) return;
    
    setLoading(true);
    try {
      // Fetch all business plans (regular endpoint)
      const response = await axios.get(`${BACKEND_URL}/api/business-plans`);
      const allPlans = response.data || [];
      
      // Try to match by company_name or subsidiary reference
      const subsidiaryPlans = allPlans.filter(plan => 
        plan.company_name?.toLowerCase().includes(subsidiary.name.toLowerCase()) ||
        plan.name?.toLowerCase().includes(subsidiary.name.toLowerCase())
      );
      
      setBusinessPlans(subsidiaryPlans);
    } catch (error) {
      console.error("Error fetching subsidiary plans:", error);
      setBusinessPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const [createFormData, setCreateFormData] = useState({
    name: "",
    company_name: "",
    currency: "USD",
    fiscal_year_start: 1
  });

  // Pre-fill company name with subsidiary name when opening create dialog
  useEffect(() => {
    if (showCreateDialog && subsidiary) {
      setCreateFormData(prev => ({
        ...prev,
        company_name: subsidiary.name
      }));
    }
  }, [showCreateDialog, subsidiary]);

  const handleSwitchToPlan = (plan) => {
    if (onSwitchPlan) {
      onSwitchPlan(plan);
      toast.success(`Switched to ${plan.name}`);
      onClose();
    } else {
      // Fallback: navigate to dashboard with plan
      window.location.href = `/dashboard`;
    }
  };

  const handleCreateNewPlan = () => {
    // Open the create plan dialog
    setShowCreateDialog(true);
  };

  const handleSubmitNewPlan = async (e) => {
    e.preventDefault();
    
    if (!createFormData.name || !createFormData.company_name) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const newPlan = await createNewPlan(createFormData);
      
      if (newPlan) {
        toast.success("Business plan created successfully!");
        
        // Switch to the new plan
        if (onSwitchPlan) {
          onSwitchPlan(newPlan);
        }
        
        // Close both dialogs
        setShowCreateDialog(false);
        onClose();
        
        // Refresh the plans list
        fetchSubsidiaryPlans();
      }
    } catch (error) {
      console.error("Error creating business plan:", error);
      toast.error(error.response?.data?.detail || "Failed to create business plan");
    } finally {
      setLoading(false);
    }
  };

  if (!subsidiary) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Target className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <div className="text-xl font-bold">Business Plans</div>
                <div className="text-sm text-gray-500 font-normal mt-1">
                  {subsidiary.name}
                  {subsidiary.location && ` • ${subsidiary.location}`}
                </div>
              </div>
            </div>
            <Button size="sm" onClick={handleCreateNewPlan}>
              <Plus className="h-4 w-4 mr-2" />
              New Plan
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="py-12 text-center text-gray-500">
              <Target className="h-8 w-8 animate-spin mx-auto mb-2" />
              Loading business plans...
            </div>
          ) : businessPlans.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">
                  Found {businessPlans.length} business plan{businessPlans.length > 1 ? 's' : ''} for this subsidiary
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {businessPlans.map((plan) => {
                  const isCurrentPlan = currentPlan?.id === plan.id;
                  
                  return (
                    <Card key={plan.id} className={`transition-all ${isCurrentPlan ? 'border-2 border-blue-500 bg-blue-50' : 'hover:shadow-md'}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isCurrentPlan ? 'bg-blue-100' : 'bg-purple-100'}`}>
                                <Target className={`h-6 w-6 ${isCurrentPlan ? 'text-blue-600' : 'text-purple-600'}`} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-lg">{plan.name}</h3>
                                  {isCurrentPlan && (
                                    <Badge variant="default" className="bg-blue-500">
                                      <Check className="h-3 w-3 mr-1" />
                                      Active
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500">{plan.company_name}</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4 mt-4">
                              <div className="flex items-center gap-2">
                                <DollarSign className="h-4 w-4 text-gray-400" />
                                <div>
                                  <p className="text-xs text-gray-500">Currency</p>
                                  <p className="text-sm font-medium">{plan.currency}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <div>
                                  <p className="text-xs text-gray-500">Fiscal Year Starts</p>
                                  <p className="text-sm font-medium">
                                    {new Date(2024, plan.fiscal_year_start - 1).toLocaleString('default', { month: 'long' })}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <div>
                                  <p className="text-xs text-gray-500">Created</p>
                                  <p className="text-sm font-medium">
                                    {new Date(plan.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="mt-4 pt-4 border-t flex gap-2">
                              {!isCurrentPlan && (
                                <Button 
                                  size="sm" 
                                  onClick={() => handleSwitchToPlan(plan)}
                                  className="flex-1"
                                >
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                  Switch to This Plan
                                </Button>
                              )}
                              {isCurrentPlan && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => window.location.href = '/dashboard'}
                                  className="flex-1"
                                >
                                  View in Dashboard
                                </Button>
                              )}
                            </div>

                            {plan.updated_at && new Date(plan.updated_at).getTime() !== new Date(plan.created_at).getTime() && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-xs text-gray-500">
                                  Last updated: {new Date(plan.updated_at).toLocaleDateString()}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="py-12 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Business Plans Found</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                No business plans are linked to "{subsidiary.name}" yet. Create a new strategic plan to get started.
              </p>
              <Button onClick={handleCreateNewPlan}>
                <Plus className="h-4 w-4 mr-2" />
                Create Business Plan
              </Button>
              <p className="text-xs text-gray-400 mt-3">
                Creates a new business plan linked to this subsidiary
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>

      {/* Create Business Plan Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Business Plan for {subsidiary?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitNewPlan} className="space-y-4 py-4">
            <div>
              <Label htmlFor="plan-name">Plan Name *</Label>
              <Input
                id="plan-name"
                value={createFormData.name}
                onChange={(e) => setCreateFormData({...createFormData, name: e.target.value})}
                placeholder="e.g., 2025 Strategic Plan"
                required
              />
            </div>

            <div>
              <Label htmlFor="company-name">Company Name *</Label>
              <Input
                id="company-name"
                value={createFormData.company_name}
                onChange={(e) => setCreateFormData({...createFormData, company_name: e.target.value})}
                placeholder="e.g., TavMentors"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Pre-filled with subsidiary name
              </p>
            </div>

            <div>
              <Label htmlFor="currency">Currency</Label>
              <Select
                value={createFormData.currency}
                onValueChange={(value) => setCreateFormData({...createFormData, currency: value})}
              >
                <SelectTrigger id="currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                  <SelectItem value="GBP">GBP - British Pound</SelectItem>
                  <SelectItem value="JPY">JPY - Japanese Yen</SelectItem>
                  <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                  <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                  <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                  <SelectItem value="CNY">CNY - Chinese Yuan</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="fiscal-year">Fiscal Year Start</Label>
              <Select
                value={createFormData.fiscal_year_start.toString()}
                onValueChange={(value) => setCreateFormData({...createFormData, fiscal_year_start: parseInt(value)})}
              >
                <SelectTrigger id="fiscal-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <SelectItem key={month} value={month.toString()}>
                      {new Date(2024, month - 1).toLocaleString('default', { month: 'long' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Creating..." : "Create Plan"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default SubsidiaryPlansModal;
